import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    createGrant,
    estimateGrantImpact,
    groupKeyId,
    listAccessChanges,
    listAccessDenials,
    listGrantRoles,
    listGrantGroups,
    listGrantViewers,
    listGrants,
    newOperationId,
    revokeGrant,
    updateGrantScope,
} from "../../lib/accessClient.js";
import { denialOrRawMessage } from "../../lib/denialReason.js";
import { hasSensitivePermission, invalidateMyPermissions, useMyPermissions } from "../../lib/permissions.js";
import { PERMISSIONS } from "../../lib/permissionKeys.js";
import { formatCompact } from "../../lib/renderers.js";
import { roleLabel } from "../../lib/userRoles.js";
import { useDataTableColumns } from "../../lib/columnSelection.js";
import Select from "../Select.jsx";
import DataTable from "../DataTable.jsx";
import EmptyState from "../EmptyState.jsx";
import SectionActions, { SECTION_BUTTON } from "../settings/SectionActions.jsx";
import Checkbox from "../ui/Checkbox.jsx";
import SearchInput from "../ui/SearchInput.jsx";
import Tabs from "../ui/Tabs.jsx";
import Panel from "../ui/Panel.jsx";
import TableBar, { Count } from "../ui/TableBar.jsx";
import { ErrorText } from "../ui/Notice.jsx";
import Button from "../ui/Button.jsx";
import ConfirmDialog from "../ConfirmDialog.jsx";
import { DIALOG } from "../../lib/analytics/surfaceVocabulary.js";

const NOTE_CLASS = "text-[12px] leading-[1.45] text-[color:var(--muted)]";
// A named part inside the editor or a grant's detail (a picker, a summary).
const PART_LABEL = "text-[12.5px] font-medium leading-[1.3] text-[color:var(--ink)]";

const PAGE_SIZE = 25;
const PROVIDER_STORAGE = {
    ENTRA_ID: "entra_id",
    OKTA: "okta",
    GOOGLE: "google_workspace",
    DIRECTORY_PROVIDER_TYPE_ENTRA_ID: "entra_id",
    DIRECTORY_PROVIDER_TYPE_OKTA: "okta",
    DIRECTORY_PROVIDER_TYPE_GOOGLE_WORKSPACE: "google_workspace",
};

// The grants table's columns; the first names what receives the grant.
const grantColumns = (recipient) => [
    { id: "grantee", label: recipient, width: 240 },
    { id: "role", label: "Role", width: 150 },
    { id: "scope", label: "Visible groups", flex: true },
    { id: "employees", label: "Employees", width: 110 },
    { id: "_actions", label: "Actions", width: 230 },
];
const GRANT_COLUMNS = { groups: grantColumns("Group"), users: grantColumns("User") };
const GRANT_COLUMN_IDS = GRANT_COLUMNS.groups.map((column) => column.id);
const GRANT_TYPES = [{ id: "groups", label: "Groups" }, { id: "users", label: "Users" }];

function useLatestRef(value) {
    const ref = useRef(value);
    ref.current = value;
    return ref;
}

function wireDirectoryGroup(group) {
    return {
        sourceProvider: PROVIDER_STORAGE[group.sourceProvider] ?? group.sourceProvider,
        integrationId: group.integrationId ?? "",
        sourceId: group.sourceId ?? "",
    };
}

function dateTime(ms) {
    if (!ms) return "Unknown time";
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? "Unknown time" : date.toLocaleString();
}

function Badge({ tone = "neutral", children }) {
    const toneClass = tone === "danger"
        ? "access-badge--danger"
        : tone === "warn"
            ? "access-badge--warn"
            : "border-[color:var(--line)] bg-[color:var(--control-bg)] text-[color:var(--muted)]";
    return <span className={`inline-flex rounded-[5px] border px-[6px] py-[1px] text-[12px] leading-[1.35] ${toneClass}`}>{children}</span>;
}

function ResponsiveField({ label, children, className = "", valueClassName = "" }) {
    return (
        <div className={`grid min-w-0 grid-cols-[104px_minmax(0,1fr)] items-start gap-[8px] @min-[880px]:block ${className}`}>
            <span className="pt-[1px] text-[12px] font-medium text-[color:var(--muted)] @min-[880px]:hidden">{label}</span>
            <div className={`min-w-0 ${valueClassName}`}>{children}</div>
        </div>
    );
}

function LoadingMore({ loading, nextPageToken, shown, total, totalCapped = false, onLoad }) {
    if (!nextPageToken) return null;
    return (
        <Button size="sm" className="justify-self-start" onClick={onLoad} disabled={loading}>
            {loading ? "Loading…" : totalCapped
                ? `Load more (${formatCompact(shown)} loaded)`
                : `Load more (${formatCompact(shown)} of ${formatCompact(total)})`}
        </Button>
    );
}

function PersonCount({ value, label, plural = `${label}s` }) {
    const accessibleLabel = `${formatCompact(value)} ${Number(value) === 1 ? label : plural}`;
    return (
        <span className="inline-flex items-center gap-[4px] tabular-nums" aria-label={accessibleLabel}>
            <svg viewBox="0 0 16 16" aria-hidden="true" className="h-[11px] w-[11px]" fill="none" stroke="currentColor" strokeWidth="1.4">
                <circle cx="8" cy="5" r="2.2" />
                <path d="M3.8 13c.4-2.5 1.8-3.8 4.2-3.8s3.8 1.3 4.2 3.8" />
            </svg>
            {formatCompact(value)}
        </span>
    );
}

function countNoun(value, singular, plural = `${singular}s`) {
    return `${formatCompact(value)} ${Number(value) === 1 ? singular : plural}`;
}

function DrilldownList({ kind, grant }) {
    const capsRef = useLatestRef(useMyPermissions());
    const isDenials = kind === "denials";
    const viewerId = grant.grantee.type === "viewer" ? grant.grantee.viewerUserId : "";
    const [state, setState] = useState({ loading: true, rows: [], next: "", total: 0, totalCapped: false, error: "" });
    const load = useCallback(async (token = "") => {
        if (isDenials && !viewerId) {
            setState({ loading: false, rows: [], next: "", total: 0, totalCapped: false, error: "" });
            return;
        }
        setState((current) => ({ ...current, loading: true, error: "" }));
        try {
            const page = isDenials
                ? await listAccessDenials(viewerId, { pageSize: 20, pageToken: token })
                : await listAccessChanges(grant.id, { pageSize: 20, pageToken: token });
            const rows = isDenials ? page.denials : page.changes;
            setState((current) => ({
                loading: false,
                rows: token ? [...current.rows, ...rows] : rows,
                next: page.nextPageToken,
                total: page.totalCount,
                totalCapped: Boolean(page.totalCountCapped),
                error: "",
            }));
        } catch (error) {
            setState((current) => ({ ...current, loading: false, error: denialOrRawMessage(error, capsRef.current) }));
        }
    }, [capsRef, grant.id, isDenials, viewerId]);
    useEffect(() => { load(); }, [load]);

    if (isDenials && !viewerId) {
        return <span className={NOTE_CLASS}>Recent failed access checks are available for user grants only.</span>;
    }
    return (
        <div aria-busy={state.loading} className="@container grid gap-[6px]">
            <span className={NOTE_CLASS}>
                Showing {formatCompact(state.rows.length)} of {formatCompact(state.total)}{state.totalCapped ? "+" : ""}
            </span>
            <ErrorText>{state.error}</ErrorText>
            {state.rows.length ? (
                <div className="border-b border-[color:var(--line-soft)]">
                    <div className="hidden @min-[880px]:grid @min-[880px]:grid-cols-[minmax(130px,0.7fr)_minmax(160px,1fr)_minmax(120px,0.7fr)] gap-[8px] border-b border-[color:var(--line-soft)] px-[6px] py-[6px] text-[12px] font-medium text-[color:var(--muted)]">
                        <span>Time</span>
                        <span>{isDenials ? "Request" : "Change"}</span>
                        <span>{isDenials ? "Reason" : "Changed by"}</span>
                    </div>
                    {state.rows.map((row, index) => (
                        <div key={row.changeId || `${row.occurredAt}-${row.rpcName}-${index}`} className="grid gap-[6px] border-t border-[color:var(--line-soft)] px-[6px] py-[5px] text-[12.5px] first:border-t-0 @min-[880px]:grid-cols-[minmax(130px,0.7fr)_minmax(160px,1fr)_minmax(120px,0.7fr)]">
                            <ResponsiveField label="Time"><span className="text-[color:var(--muted)]">{dateTime(row.occurredAt)}</span></ResponsiveField>
                            <ResponsiveField label={isDenials ? "Request" : "Change"}><span className="text-[color:var(--ink)]">{isDenials ? row.rpcName : `${row.roleSlug} grant ${row.action}`}</span></ResponsiveField>
                            <ResponsiveField label={isDenials ? "Reason" : "Changed by"}><span className="text-[color:var(--muted)]">{isDenials ? `${row.reason}${row.coalescedCount > 1 ? `, ${row.coalescedCount} times` : ""}` : row.actorDisplay}</span></ResponsiveField>
                        </div>
                    ))}
                </div>
            ) : null}
            {!state.loading && !state.rows.length ? <span className={NOTE_CLASS}>{isDenials ? "No recent failed access checks for this user." : "No changes have been recorded for this grant."}</span> : null}
            <LoadingMore loading={state.loading} nextPageToken={state.next} shown={state.rows.length} total={state.total} totalCapped={state.totalCapped} onLoad={() => load(state.next)} />
        </div>
    );
}

function GrantDrilldown({ grant }) {
    const [kind, setKind] = useState("audit");
    const isGroupGrant = grant.grantee.type === "group";
    return (
        <div className="grid min-w-0 gap-[8px] py-[6px]">
            {isGroupGrant ? (
                <>
                    <h4 className={`m-0 ${PART_LABEL}`}>Audit trail</h4>
                    <DrilldownList key={`${grant.id}-audit`} kind="audit" grant={grant} />
                </>
            ) : (
                <>
                    <Tabs
                        label={`Grant history for ${grant.grantee.display}`}
                        items={[{ id: "audit", label: "Audit trail" }, { id: "denials", label: "Recent denials" }]}
                        value={kind}
                        onChange={setKind}
                        className="justify-self-start"
                    />
                    <DrilldownList key={`${grant.id}-${kind}`} kind={kind} grant={grant} />
                </>
            )}
        </div>
    );
}

function CandidateList({ busy, rows, total, next, selectedId, getId, render, onSelect, onMore, degraded = false, error = "" }) {
    return (
        <div aria-busy={busy} className="grid gap-[4px] rounded-[10px] border border-[color:var(--line-soft)] p-[8px]">
            <span className={NOTE_CLASS}>Showing {formatCompact(rows.length)} of {formatCompact(total)}</span>
            {rows.map((row) => {
                const id = getId(row);
                return (
                    <label key={id} className="flex cursor-pointer items-start gap-[8px] rounded-[6px] px-[4px] py-[5px] text-[12.5px] text-[color:var(--ink)] hover:bg-[color-mix(in_srgb,var(--ink)_6%,transparent)]">
                        <input type="radio" checked={selectedId === id} onChange={() => onSelect(row)} />
                        <span className="min-w-0 flex-1">{render(row)}</span>
                    </label>
                );
            })}
            <ErrorText>{error}</ErrorText>
            {!busy && !degraded && !error && !rows.length ? <span className={NOTE_CLASS}>No matches.</span> : null}
            <LoadingMore loading={busy} nextPageToken={next} shown={rows.length} total={total} onLoad={onMore} />
        </div>
    );
}

function groupDisplayName(group, annotations = []) {
    return annotations.find((entry) => groupKeyId(entry.group) === groupKeyId(group))?.displayName
        || group.displayName || group.sourceId;
}

function GrantEditor({ grant, initialGranteeType = "viewer", onCancel, onSaved }) {
    const capsRef = useLatestRef(useMyPermissions());
    const editing = Boolean(grant);
    const [granteeType, setGranteeType] = useState(grant?.grantee.type ?? initialGranteeType);
    const [selectedViewer, setSelectedViewer] = useState(grant?.grantee.type === "viewer" ? {
        viewerUserId: grant.grantee.viewerUserId,
        displayName: grant.grantee.display,
        hasSubjectMapping: true,
    } : null);
    const [selectedGranteeGroup, setSelectedGranteeGroup] = useState(grant?.grantee.type === "group" ? {
        ...grant.grantee.directoryGroup,
        displayName: grant.grantee.display,
        memberCount: grant.granteeMemberCount,
    } : null);
    const [roleId, setRoleId] = useState("");
    const [selectedGroups, setSelectedGroups] = useState(() => new Map(
        (grant?.scopeGroups ?? []).map((group) => [groupKeyId(group), { ...group, displayName: groupDisplayName(group, grant?.scopeAnnotations) }]),
    ));
    const [recipientGroupSearch, setRecipientGroupSearch] = useState("");
    const [scopeGroupSearch, setScopeGroupSearch] = useState("");
    const [viewerSearch, setViewerSearch] = useState("");
    const [recipientGroups, setRecipientGroups] = useState({ loading: !editing, rows: [], next: "", total: 0, degraded: false, error: "" });
    const [scopeCandidates, setScopeCandidates] = useState({ loading: true, rows: [], next: "", total: 0, degraded: false, error: "" });
    const [viewers, setViewers] = useState({ loading: !editing, rows: [], next: "", total: 0, error: "" });
    const [roles, setRoles] = useState({ loading: !editing, rows: [], next: "", total: 0, error: "" });
    const [impact, setImpact] = useState({ loading: false, value: null, error: "" });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const recipientGroupRequest = useRef(0);
    const scopeGroupRequest = useRef(0);
    const viewerRequest = useRef(0);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const loadRecipientGroups = useCallback(async (token = "") => {
        if (editing || granteeType !== "group") return;
        const request = ++recipientGroupRequest.current;
        setRecipientGroups((current) => token
            ? { ...current, loading: true, error: "" }
            : { loading: true, rows: [], next: "", total: 0, degraded: false, error: "" });
        try {
            const page = await listGrantGroups({ search: recipientGroupSearch, pageSize: PAGE_SIZE, pageToken: token });
            setRecipientGroups((current) => recipientGroupRequest.current === request
                ? {
                    loading: false,
                    rows: token ? [...current.rows, ...page.groups] : page.groups,
                    next: page.nextPageToken,
                    total: page.totalCount,
                    degraded: page.degraded,
                    error: "",
                }
                : current);
        } catch (failure) {
            const message = denialOrRawMessage(failure, capsRef.current);
            setRecipientGroups((current) => recipientGroupRequest.current === request
                ? { ...current, loading: false, error: message }
                : current);
        }
    }, [capsRef, editing, granteeType, recipientGroupSearch]);
    useEffect(() => {
        if (editing || granteeType !== "group") return undefined;
        const timer = setTimeout(() => loadRecipientGroups(), 180);
        return () => {
            clearTimeout(timer);
            recipientGroupRequest.current += 1;
        };
    }, [editing, granteeType, loadRecipientGroups]);

    const loadScopeGroups = useCallback(async (token = "") => {
        const request = ++scopeGroupRequest.current;
        setScopeCandidates((current) => token
            ? { ...current, loading: true, error: "" }
            : { loading: true, rows: [], next: "", total: 0, degraded: false, error: "" });
        try {
            const page = await listGrantGroups({ search: scopeGroupSearch, pageSize: PAGE_SIZE, pageToken: token });
            setScopeCandidates((current) => scopeGroupRequest.current === request
                ? {
                    loading: false,
                    rows: token ? [...current.rows, ...page.groups] : page.groups,
                    next: page.nextPageToken,
                    total: page.totalCount,
                    degraded: page.degraded,
                    error: "",
                }
                : current);
        } catch (failure) {
            const message = denialOrRawMessage(failure, capsRef.current);
            setScopeCandidates((current) => scopeGroupRequest.current === request
                ? { ...current, loading: false, error: message }
                : current);
        }
    }, [capsRef, scopeGroupSearch]);
    useEffect(() => {
        const timer = setTimeout(() => loadScopeGroups(), 180);
        return () => {
            clearTimeout(timer);
            scopeGroupRequest.current += 1;
        };
    }, [loadScopeGroups]);

    const loadViewers = useCallback(async (token = "") => {
        if (editing || granteeType !== "viewer") return;
        const request = ++viewerRequest.current;
        setViewers((current) => token
            ? { ...current, loading: true, error: "" }
            : { loading: true, rows: [], next: "", total: 0, error: "" });
        try {
            const page = await listGrantViewers({ pageSize: PAGE_SIZE, pageToken: token, search: viewerSearch });
            setViewers((current) => viewerRequest.current === request
                ? { loading: false, rows: token ? [...current.rows, ...page.viewers] : page.viewers, next: page.nextPageToken, total: page.totalCount, error: "" }
                : current);
        } catch (failure) {
            const message = denialOrRawMessage(failure, capsRef.current);
            setViewers((current) => viewerRequest.current === request
                ? { ...current, loading: false, error: message }
                : current);
        }
    }, [capsRef, editing, granteeType, viewerSearch]);
    useEffect(() => {
        if (editing || granteeType !== "viewer") return undefined;
        const timer = setTimeout(() => loadViewers(), 180);
        return () => {
            clearTimeout(timer);
            viewerRequest.current += 1;
        };
    }, [editing, granteeType, loadViewers]);

    const loadRoles = useCallback(async (token = "") => {
        if (editing) return;
        setRoles((current) => ({ ...current, loading: true, error: "" }));
        try {
            const page = await listGrantRoles({ pageSize: 50, pageToken: token });
            setRoles((current) => ({ loading: false, rows: token ? [...current.rows, ...page.roles] : page.roles, next: page.nextPageToken, total: page.totalCount, error: "" }));
        } catch (failure) {
            setRoles((current) => ({ ...current, loading: false, error: denialOrRawMessage(failure, capsRef.current) }));
        }
    }, [capsRef, editing]);
    useEffect(() => { if (!editing) loadRoles(); }, [editing, loadRoles]);

    const granteeValue = useMemo(() => {
        if (granteeType === "viewer" && selectedViewer) return { type: "viewer", viewerUserId: selectedViewer.viewerUserId };
        if (granteeType === "group" && selectedGranteeGroup) return { type: "group", directoryGroup: wireDirectoryGroup(selectedGranteeGroup) };
        return null;
    }, [granteeType, selectedGranteeGroup, selectedViewer]);
    const scopeGroups = useMemo(() => [...selectedGroups.values()].map(wireDirectoryGroup), [selectedGroups]);
    const viewerNeedsMapping = granteeType === "viewer" && selectedViewer && !selectedViewer.hasSubjectMapping;

    useEffect(() => {
        if (!granteeValue || !scopeGroups.length) {
            setImpact({ loading: false, value: null, error: "" });
            return undefined;
        }
        let cancelled = false;
        const timer = setTimeout(() => {
            setImpact({ loading: true, value: null, error: "" });
            estimateGrantImpact({ grantee: granteeValue, scopeGroups }).then(
                (value) => { if (!cancelled) setImpact({ loading: false, value, error: "" }); },
                (failure) => { if (!cancelled) setImpact({ loading: false, value: null, error: denialOrRawMessage(failure, capsRef.current) }); },
            );
        }, 180);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [capsRef, granteeValue, scopeGroups]);

    const toggleScope = (group) => {
        const key = groupKeyId(group);
        setSelectedGroups((current) => {
            const next = new Map(current);
            if (next.has(key)) next.delete(key);
            else next.set(key, group);
            return next;
        });
    };
    const canSave = Boolean(
        !saving
        && granteeValue
        && (editing || roleId)
        && scopeGroups.length
        && !recipientGroups.degraded
        && !scopeCandidates.degraded
        && impact.value
        && !impact.value.degraded
        && impact.value.granteeCount > 0
        && impact.value.subjectCount > 0,
    );
    const save = async () => {
        if (!canSave) return;
        setSaving(true);
        setError("");
        const operationId = newOperationId();
        try {
            if (editing) {
                await updateGrantScope({ bindingTable: grant.bindingTable, bindingId: grant.id, scopeGroups, operationId });
            } else {
                await createGrant({ grantee: granteeValue, roleId, scopeGroups, operationId });
            }
            if (!mounted.current) return;
            invalidateMyPermissions();
            onSaved(granteeType);
        } catch (failure) {
            if (!mounted.current) return;
            setError(denialOrRawMessage(failure, capsRef.current));
            setSaving(false);
        }
    };

    const editorTitle = editing ? `Edit ${grant.grantee.display}` : "Grant activity access";
    const selectedGranteeId = granteeType === "viewer"
        ? selectedViewer?.viewerUserId
        : groupKeyId(selectedGranteeGroup);
    return (
        <Panel as="section" aria-label={editorTitle} aria-busy={saving} title={editorTitle} meta={editing ? "Edit grant" : "New grant"} className="gap-[16px]"
            actions={<Button size="sm" onClick={onCancel} disabled={saving}>Cancel</Button>}>
            {!editing ? (
                <Tabs
                    label="Grant recipient type"
                    items={[{ id: "group", label: "Group" }, { id: "viewer", label: "User" }]}
                    value={granteeType}
                    onChange={setGranteeType}
                    className="justify-self-start"
                />
            ) : (
                <span className={NOTE_CLASS}>User or group and role stay unchanged: {grant.grantee.display} - {roleLabel(grant.roleSlug)}</span>
            )}

            {granteeType === "group" ? (
                <p className={`m-0 ${NOTE_CLASS}`}>
                    Give every member of a group access with one rule. After directory sync, new members receive access and people who leave lose access through this rule. Other grants still apply.
                    {" "}Members need a dashboard account linked to their directory identity to use this access.
                </p>
            ) : null}

            <div data-grant-picker-workspace className="@container max-h-[420px] overflow-y-auto pr-[4px]">
                <div className={`grid items-start gap-[10px] ${editing ? "" : "@min-[880px]:grid-cols-2"}`}>
                    {!editing ? (
                        <section data-grant-recipient-picker className="grid gap-[7px]">
                            <span className={PART_LABEL}>{granteeType === "group" ? "1. Group receiving access" : "User receiving access"}</span>
                            {granteeType === "viewer" ? (
                                <>
                                    <SearchInput value={viewerSearch} onChange={setViewerSearch} placeholder="Search users..." label="Search grant recipients" />
                                    <CandidateList
                                        busy={viewers.loading}
                                        rows={viewers.rows}
                                        total={viewers.total}
                                        next={viewers.next}
                                        selectedId={selectedGranteeId}
                                        getId={(viewer) => viewer.viewerUserId}
                                        onSelect={setSelectedViewer}
                                        onMore={() => loadViewers(viewers.next)}
                                        error={viewers.error}
                                        render={(viewer) => <span>{viewer.displayName || viewer.viewerUserId}{!viewer.hasSubjectMapping ? <span className="ml-[6px]"><Badge tone="warn">Employee not linked</Badge></span> : null}</span>}
                                    />
                                </>
                            ) : (
                                <>
                                    <SearchInput value={recipientGroupSearch} onChange={setRecipientGroupSearch} placeholder="Search groups receiving access..." label="Search group recipients" />
                                    {recipientGroups.degraded ? <Badge tone="warn">Directory data unavailable</Badge> : null}
                                    <CandidateList
                                        busy={recipientGroups.loading}
                                        rows={recipientGroups.rows}
                                        total={recipientGroups.total}
                                        next={recipientGroups.next}
                                        selectedId={selectedGranteeId}
                                        getId={groupKeyId}
                                        onSelect={setSelectedGranteeGroup}
                                        onMore={() => loadRecipientGroups(recipientGroups.next)}
                                        degraded={recipientGroups.degraded}
                                        error={recipientGroups.error}
                                        render={(group) => <span>{group.displayName} - {countNoun(group.memberCount, "person", "people")}</span>}
                                    />
                                </>
                            )}
                        </section>
                    ) : null}

                    <section data-grant-scope-picker className="grid gap-[7px]">
                        <div className={`grid gap-[7px] ${PART_LABEL}`}>
                            {granteeType === "group" ? `${editing ? "" : "2. "}Groups whose activity they can see` : "Visible employees and groups"}
                            <SearchInput value={scopeGroupSearch} onChange={setScopeGroupSearch} placeholder="Search visible groups..." label="Search visible groups" />
                        </div>
                        <div aria-busy={scopeCandidates.loading} className="grid gap-[4px] rounded-[10px] border border-[color:var(--line-soft)] p-[8px]">
                            <span className={NOTE_CLASS}>Showing {formatCompact(scopeCandidates.rows.length)} of {formatCompact(scopeCandidates.total)} groups - {selectedGroups.size} selected</span>
                            {scopeCandidates.degraded ? <span className="text-[12px] text-[color:var(--severity-medium)]">Directory data is temporarily unavailable. Try again before saving.</span> : null}
                            <ErrorText>{scopeCandidates.error}</ErrorText>
                            {scopeCandidates.rows.map((group) => {
                                const id = groupKeyId(group);
                                return (
                                    <Checkbox
                                        key={id}
                                        checked={selectedGroups.has(id)}
                                        onChange={() => toggleScope(group)}
                                        label={<span>{group.displayName} - {countNoun(group.memberCount, "person", "people")}</span>}
                                        className="rounded-[6px] px-[4px] py-[5px]"
                                    />
                                );
                            })}
                            <LoadingMore loading={scopeCandidates.loading} nextPageToken={scopeCandidates.next} shown={scopeCandidates.rows.length} total={scopeCandidates.total} onLoad={() => loadScopeGroups(scopeCandidates.next)} />
                        </div>
                    </section>
                </div>
            </div>

            {!editing ? (
                <label className={`grid gap-[7px] ${PART_LABEL}`}>
                    {granteeType === "group" ? "3. Role for this access" : "Role"}
                    <Select value={roleId} onChange={setRoleId} disabled={roles.loading} placeholder="Choose a role..."
                        options={roles.rows.map((role) => ({ value: role.roleId, label: roleLabel(role.roleSlug) }))} />
                    <span className={NOTE_CLASS}>Grants include only permissions limited to the selected groups. Tenant-wide administration is excluded. Showing {formatCompact(roles.rows.length)} of {formatCompact(roles.total)} assignable roles.</span>
                    <ErrorText>{roles.error}</ErrorText>
                    <LoadingMore loading={roles.loading} nextPageToken={roles.next} shown={roles.rows.length} total={roles.total} onLoad={() => loadRoles(roles.next)} />
                </label>
            ) : null}

            {selectedGroups.size ? (
                <div className="flex flex-wrap gap-[5px]">
                    {[...selectedGroups.entries()].map(([id, group]) => (
                        <Button key={id} size="sm" onClick={() => toggleScope(group)} title="Remove group">
                            {group.displayName || group.sourceId} x
                        </Button>
                    ))}
                </div>
            ) : null}

            <div aria-busy={impact.loading} className="grid gap-[6px] rounded-[10px] border border-[color:var(--line-soft)] bg-[color:var(--control-bg)] p-[12px]">
                <span className={PART_LABEL}>Before you save</span>
                {granteeType === "group" && selectedGranteeGroup && selectedGroups.size > 0 ? (
                    <span className="text-[12.5px] text-[color:var(--ink)]">
                        Members of <strong>{selectedGranteeGroup.displayName || selectedGranteeGroup.sourceId}</strong> receive access to activity from <strong>{[...selectedGroups.values()].map((group) => group.displayName || group.sourceId).join(", ")}</strong>.
                    </span>
                ) : null}
                {impact.loading ? <span className={NOTE_CLASS}>Calculating exact impact...</span> : impact.value?.degraded ? (
                    <><Badge tone="warn">Impact unavailable</Badge><span className={NOTE_CLASS}>Wait for directory data to recover before changing access.</span></>
                ) : impact.value ? (
                    <>
                        <strong className="text-[12.5px] font-medium text-[color:var(--ink)]">
                            {viewerNeedsMapping ? "After this dashboard account is linked, " : ""}{countNoun(impact.value.granteeCount, "user")} will be able to see activity for {countNoun(impact.value.subjectCount, "employee")}
                        </strong>
                        <span className={NOTE_CLASS}>The selected role still controls which activity they can open.</span>
                        {viewerNeedsMapping ? <span className={NOTE_CLASS}>Link this dashboard account to an employee before they can use the grant.</span> : null}
                        {impact.value.granteeCount === 0 || impact.value.subjectCount === 0 ? <Badge tone="danger">No one receives access</Badge> : null}
                    </>
                ) : <span className={NOTE_CLASS}>Choose a user or group and at least one visible group.</span>}
                <ErrorText>{impact.error}</ErrorText>
            </div>
            <span className={NOTE_CLASS}>Most grant changes take effect within a few seconds. Group membership changes take effect after the next directory sync.</span>
            <ErrorText>{error}</ErrorText>
            <div className="flex justify-end gap-[8px]">
                <Button onClick={onCancel} disabled={saving}>Cancel</Button>
                <Button variant="primary" onClick={save} disabled={!canSave}>{saving ? "Saving…" : editing ? "Save scope" : "Create grant"}</Button>
            </div>
        </Panel>
    );
}

// When impactDegraded is set, the server could not count group members and left
// every count at 0. Presence comes from a separate read and stays accurate.
function groupRecipientHealth(grant) {
    if (grant.grantee.type !== "group") return null;
    if (grant.granteePresence === "absent") return { text: "Not in directory", tone: "danger" };
    if (grant.granteePresence === "live" && !grant.impactDegraded && grant.granteeMemberCount === 0) return { text: "No active members", tone: "danger" };
    return null;
}

function scopeBadges(grant) {
    const badges = [];
    if (grant.impactDegraded) badges.push({ text: "Count unavailable", tone: "warn" });
    for (const annotation of grant.scopeAnnotations) {
        if (annotation.presence === "absent") badges.push({ text: `${annotation.displayName || annotation.group?.sourceId || "Group"}: Not in directory`, tone: "danger" });
        else if (annotation.presence === "live" && !grant.impactDegraded && annotation.memberCount === 0) badges.push({ text: `${annotation.displayName || annotation.group?.sourceId || "Group"}: No active members`, tone: "danger" });
    }
    if (!grant.impactDegraded && grant.effectiveSubjectCount === 0 && !groupRecipientHealth(grant) && !badges.length) badges.push({ text: "No visible employees", tone: "danger" });
    return badges;
}

function GrantBadgeList({ badges }) {
    if (!badges.length) return null;
    return <div className="flex flex-wrap gap-[4px]">{badges.map((badge, index) => <Badge key={`${badge.text}-${index}`} tone={badge.tone}>{badge.text}</Badge>)}</div>;
}

export default function AccessTab({ selectedGrant = null, onClearSelectedGrant }) {
    const caps = useMyPermissions();
    const capsRef = useLatestRef(caps);
    const canManage = hasSensitivePermission(caps, PERMISSIONS.GRANTS_MANAGE);
    const canBrowsePeople = hasSensitivePermission(caps, PERMISSIONS.DIRECTORY_DATA_READ);
    const canEdit = canManage && canBrowsePeople;
    const [grantSearchInput, setGrantSearchInput] = useState("");
    const [grantSearch, setGrantSearch] = useState("");
    const [grantType, setGrantType] = useState(selectedGrant?.grantee.type === "viewer" ? "users" : "groups");
    const [refresh, setRefresh] = useState(0);
    const [state, setState] = useState({ loading: true, rows: [], next: "", total: 0, error: "" });
    const [editor, setEditor] = useState(null);
    const editorRef = useRef(null);
    const [expanded, setExpanded] = useState("");
    const [revokeTarget, setRevokeTarget] = useState(null);
    const [revoking, setRevoking] = useState(false);
    const [revokeError, setRevokeError] = useState("");
    const listRequest = useRef(0);
    useEffect(() => {
        const timer = setTimeout(() => setGrantSearch(grantSearchInput.trim()), 220);
        return () => clearTimeout(timer);
    }, [grantSearchInput]);
    useEffect(() => {
        if (!editor) return;
        editorRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
        editorRef.current?.focus?.({ preventScroll: true });
    }, [editor]);
    useEffect(() => {
        if (canEdit) return;
        setEditor(null);
        if (!canManage) setRevokeTarget(null);
    }, [canEdit, canManage]);
    const bindingTable = grantType === "groups"
        ? "GRANT_BINDING_TABLE_DIRECTORY_GROUP_BINDING"
        : "GRANT_BINDING_TABLE_ROLE_BINDING";

    useEffect(() => {
        const request = ++listRequest.current;
        setState({ loading: true, rows: [], next: "", total: 0, error: "" });
        listGrants({ pageSize: PAGE_SIZE, search: grantSearch, bindingTable }).then(
            (page) => { if (listRequest.current === request) setState({ loading: false, rows: page.grants, next: page.nextPageToken, total: page.totalCount, error: "" }); },
            (error) => { if (listRequest.current === request) setState({ loading: false, rows: [], next: "", total: 0, error: denialOrRawMessage(error, capsRef.current) }); },
        );
        return () => { listRequest.current += 1; };
    }, [bindingTable, capsRef, grantSearch, refresh]);

    const loadMore = async () => {
        if (!state.next || state.loading) return;
        const request = ++listRequest.current;
        setState((current) => ({ ...current, loading: true, error: "" }));
        try {
            const page = await listGrants({ pageSize: PAGE_SIZE, pageToken: state.next, search: grantSearch, bindingTable });
            setState((current) => listRequest.current === request
                ? { loading: false, rows: [...current.rows, ...page.grants], next: page.nextPageToken, total: page.totalCount, error: "" }
                : current);
        } catch (error) {
            const message = denialOrRawMessage(error, capsRef.current);
            setState((current) => listRequest.current === request
                ? { ...current, loading: false, error: message }
                : current);
        }
    };
    const afterSave = (granteeType) => {
        if (granteeType) setGrantType(granteeType === "group" ? "groups" : "users");
        if (editor?.mode === "create") {
            setGrantSearchInput("");
            setGrantSearch("");
        }
        setEditor(null);
        onClearSelectedGrant?.();
        setExpanded("");
        setRefresh((value) => value + 1);
    };
    const revoke = async () => {
        if (!revokeTarget || revoking) return;
        setRevoking(true);
        setRevokeError("");
        const operationId = newOperationId();
        try {
            await revokeGrant({ bindingTable: revokeTarget.bindingTable, bindingId: revokeTarget.id, operationId });
            invalidateMyPermissions();
            setRevokeTarget(null);
            setRevoking(false);
            afterSave();
        } catch (error) {
            setRevokeError(denialOrRawMessage(error, capsRef.current));
            setRevoking(false);
        }
    };

    const cols = useDataTableColumns({ storageKey: "accessGrants", columns: GRANT_COLUMNS[grantType], defaultVisible: GRANT_COLUMN_IDS });
    // An opened grant's audit trail is a row of its own under the grant.
    const tableRows = useMemo(
        () => state.rows.flatMap((grant) => (expanded === grant.id ? [grant, { id: `${grant.id}:detail`, detailOf: grant }] : [grant])),
        [state.rows, expanded],
    );
    const renderCell = (grant, column) => {
        switch (column.id) {
            case "grantee":
                return (
                    <div className="grid min-w-0 gap-[2px]">
                        <strong className="truncate text-[12.5px] font-medium text-[color:var(--ink)]">{grant.grantee.display}</strong>
                        {grant.grantee.type === "group" && !grant.impactDegraded ? <span className={NOTE_CLASS}><PersonCount value={grant.granteeMemberCount} label="group member" /></span> : null}
                        <GrantBadgeList badges={[groupRecipientHealth(grant)].filter(Boolean)} />
                    </div>
                );
            case "role":
                return <span className="block min-w-0 truncate whitespace-nowrap text-[12.5px] text-[color:var(--ink)]" title={grant.roleSlug}>{roleLabel(grant.roleSlug)}</span>;
            case "scope":
                return (
                    <div className="grid min-w-0 gap-[3px]">
                        <span className="text-[12.5px] text-[color:var(--ink)]">{grant.scopeGroups.map((group) => groupDisplayName(group, grant.scopeAnnotations)).join(", ") || "-"}</span>
                        <GrantBadgeList badges={scopeBadges(grant)} />
                    </div>
                );
            case "employees":
                return <span className="text-[12.5px] text-[color:var(--muted)]">{grant.impactDegraded ? "-" : <PersonCount value={grant.effectiveSubjectCount} label="visible employee" />}</span>;
            case "_actions":
                return (
                    <div className="flex flex-wrap gap-[6px]">
                        <Button size="sm" aria-expanded={expanded === grant.id} onClick={() => setExpanded(expanded === grant.id ? "" : grant.id)}>{expanded === grant.id ? "Close" : "Details"}</Button>
                        {canEdit ? <Button size="sm" onClick={() => setEditor({ mode: "edit", grant })}>Edit</Button> : null}
                        {canManage ? <Button size="sm" variant="danger-ghost" onClick={() => { setRevokeError(""); setRevokeTarget(grant); }}>Revoke</Button> : null}
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <>
            {canEdit && !editor ? (
                <SectionActions>
                    <button type="button" className={SECTION_BUTTON} onClick={() => setEditor({ mode: "create", granteeType: "group" })}>Add grant</button>
                </SectionActions>
            ) : null}
            <div className="flex min-w-0 flex-1 flex-col gap-[16px]">
                <p className={`m-0 max-w-[72ch] ${NOTE_CLASS}`}>
                    A grant gives a group or one user a role over the activity of the directory groups you choose. Group grants follow directory membership.
                </p>

                {selectedGrant && canBrowsePeople && !editor ? (
                    <Panel
                        as="section"
                        aria-label="Selected grant"
                        title={selectedGrant.grantee.display}
                        meta="Selected grant"
                        actions={<Button size="sm" onClick={onClearSelectedGrant}>Back to all grants</Button>}
                    >
                        <p className="m-0 text-[13.5px] leading-[1.55] text-[color:var(--ink)]">
                            {selectedGrant.grantee.type === "group" ? "Group members have" : "This user has"} <strong>{roleLabel(selectedGrant.roleSlug)}</strong> access to activity from <strong>{selectedGrant.scopeGroups.map((group) => groupDisplayName(group, selectedGrant.scopeAnnotations)).join(", ")}</strong>.
                        </p>
                        <span className={NOTE_CLASS}>Configured assignment. Current visibility also depends on other grants, roles, and whether group restrictions are enabled.</span>
                        {canEdit ? <Button className="justify-self-start" onClick={() => setEditor({ mode: "edit", grant: selectedGrant })}>Edit grant</Button> : null}
                        <div className="border-t border-[color:var(--line-soft)] pt-[8px]">
                            <GrantDrilldown grant={selectedGrant} />
                        </div>
                    </Panel>
                ) : null}

                {canEdit && editor ? (
                    <div ref={editorRef} tabIndex={-1} className="scroll-mt-[12px] outline-none" data-grant-editor-region>
                        <GrantEditor
                            key={editor.grant?.id ?? editor.granteeType ?? grantType}
                            grant={editor.grant ?? null}
                            initialGranteeType={editor.granteeType ?? "group"}
                            onCancel={() => setEditor(null)}
                            onSaved={afterSave}
                        />
                    </div>
                ) : null}

                <section aria-label={grantType === "groups" ? "Group grants" : "User grants"} aria-busy={state.loading} className="flex min-w-0 flex-1 flex-col">
                    <TableBar>
                        <Tabs
                            mode="toggle"
                            label="Grant recipient type"
                            items={GRANT_TYPES}
                            value={grantType}
                            onChange={(id) => { setGrantType(id); setExpanded(""); }}
                        />
                        <SearchInput
                            size="sm"
                            value={grantSearchInput}
                            onChange={setGrantSearchInput}
                            placeholder="Search users, groups, roles, or visible groups"
                            label="Search access grants"
                            className="w-[300px] max-w-full"
                        />
                        <Count n={state.loading && !state.rows.length ? null : state.total} one="grant" />
                    </TableBar>
                    {state.error || (!revokeTarget && revokeError) ? (
                        <div className="grid gap-[4px] pt-[8px]">
                            <ErrorText>{state.error}</ErrorText>
                            {!revokeTarget ? <ErrorText>{revokeError}</ErrorText> : null}
                        </div>
                    ) : null}
                    <DataTable
                        cols={cols}
                        rows={tableRows}
                        getRowId={(row) => row.id}
                        renderCell={renderCell}
                        renderFullRow={(row) => (row.detailOf ? <GrantDrilldown grant={row.detailOf} /> : null)}
                        loading={state.loading}
                        skeletonRows={3}
                        minWidth={820}
                        empty={state.loading ? null : (
                            <EmptyState
                                className="my-[12px]"
                                title="No grants found"
                                note={grantSearch ? "Try a different user, group, role, or visible group." : `Create a grant for a ${grantType === "groups" ? "group" : "user"}.`}
                            />
                        )}
                        footer={state.next ? (
                            <div className="pt-[8px] pb-[10px]">
                                <LoadingMore loading={state.loading} nextPageToken={state.next} shown={state.rows.length} total={state.total} onLoad={loadMore} />
                            </div>
                        ) : null}
                    />
                </section>
            </div>
            <ConfirmDialog
                open={Boolean(revokeTarget)}
                analyticsId={DIALOG.ACCESS_REVOKE}
                eyebrow="Revoke access"
                title={revokeTarget ? `Revoke grant for ${revokeTarget.grantee.display}?` : "Revoke grant?"}
                body={(
                    <div className="grid gap-[8px]">
                        <p className={`m-0 ${NOTE_CLASS}`}>This removes the grant immediately. The change will be recorded in the audit trail.</p>
                        <ErrorText>{revokeError}</ErrorText>
                    </div>
                )}
                confirmLabel={revoking ? "Revoking…" : "Revoke grant"}
                confirmDisabled={revoking}
                destructive
                onConfirm={revoke}
                onCancel={() => {
                    if (revoking) return;
                    setRevokeTarget(null);
                    setRevokeError("");
                }}
            />
        </>
    );
}
